import type { GlobalThemeOverrides } from 'naive-ui'
import { AnyColor, colord } from 'colord'
import { generate } from '@/plugins'
import { themeVars } from '@/theme/vars'

const DARK_CLASS = 'dark'

export function toggleHtmlClass(className: string) {
  function add() {
    document.documentElement.classList.add(className)
  }

  function remove() {
    document.documentElement.classList.remove(className)
  }

  return {
    add,
    remove,
  }
}
export function toggleCssDarkMode(darkMode = false) {
  const { add, remove } = toggleHtmlClass(DARK_CLASS)

  if (darkMode) {
    add()
  }
  else {
    remove()
  }
}

/**
 * Toggle grayscale mode
 *
 * @param grayscaleMode Is grayscale mode
 */
export function toggleGrayscaleMode(grayscaleMode = false) {
  const GRAYSCALE_CLASS = 'grayscale'

  const { add, remove } = toggleHtmlClass(GRAYSCALE_CLASS)

  if (grayscaleMode) {
    add()
  }
  else {
    remove()
  }
}

type NaiveColorScene = '' | 'Suppl' | 'Hover' | 'Pressed'
type NaiveColorKey = `${App.Theme.ThemeColorKey}Color${NaiveColorScene}`
type NaiveThemeColor = Partial<Record<NaiveColorKey, string>>
interface NaiveColorAction {
  scene: NaiveColorScene
  handler: (color: string) => string
}
function getNaiveThemeColors(colors: App.Theme.ThemeColor) {
  const colorActions: NaiveColorAction[] = [
    { scene: '', handler: color => color },
    { scene: 'Suppl', handler: color => colord(color).lighten(0.05).toHex() },
    { scene: 'Hover', handler: color => colord(color).lighten(0.05).toHex() },
    { scene: 'Pressed', handler: color => colord(color).darken(0.05).toHex() },
  ]

  const themeColors: NaiveThemeColor = {}

  const colorEntries = Object.entries(colors) as [App.Theme.ThemeColorKey, string][]

  colorEntries.forEach((color) => {
    colorActions.forEach((action) => {
      const [colorType, colorValue] = color
      const colorKey: NaiveColorKey = `${colorType}Color${action.scene}`
      themeColors[colorKey] = action.handler(colorValue)
    })
  })

  return themeColors
}

export function getNaiveTheme(colors: App.Theme.ThemeColor) {
  const { primary: colorLoading } = colors

  const theme: GlobalThemeOverrides = {
    common: {
      ...getNaiveThemeColors(colors),
      borderRadius: '4px',
    },
    LoadingBar: {
      colorLoading,
    },
    Tag: {
      borderRadius: '4px',
    },
  }

  return theme
}

function createThemePaletteColors(colors: App.Theme.ThemeColor, mode?: 'dark' | 'light') {
  const colorKeys = Object.keys(colors) as App.Theme.ThemeColorKey[]
  const colorPaletteVar = {} as App.Theme.ThemePaletteColor
  colorKeys.forEach((key) => {
    const colorArr = generate(colors[key], mode)
    colorPaletteVar[key] = colorArr[5]

    colorArr.forEach((hex, index) => {
      const number: App.Theme.ColorPaletteNumber = (index == 0 ? 50 : index * 100) as App.Theme.ColorPaletteNumber
      colorPaletteVar[`${key}-${number}`] = hex
    })
    return colorPaletteVar
  })

  return colorPaletteVar
}
export function createThemeToken(colors: App.Theme.ThemeColor) {
  const darkColors = createThemePaletteColors(colors, 'dark')
  const lightColors = createThemePaletteColors(colors, 'light')

  const themeTokens: App.Theme.ThemeToken = {
    colors: {
      ...lightColors,
      nprogress: lightColors.primary,
      container: 'rgb(255, 255, 255)',
      layout: 'rgb(247, 250, 252)',
      inverted: 'rgb(0, 20, 40)',
      base_text: 'rgb(31, 31, 31)',
    },

  }

  const darkThemeTokens: App.Theme.ThemeToken = {
    colors: {
      ...darkColors,
      nprogress: lightColors.primary,
      inverted: 'rgb(0, 20, 40)',
      container: 'rgb(28, 28, 28)',
      layout: 'rgb(18, 18, 18)',
      base_text: 'rgb(224, 224, 224)',
    },
  }

  return {
    themeTokens,
    darkThemeTokens,
  }
}
function getCssVarByTokens(tokens: App.Theme.BaseToken) {
  const styles: string[] = []

  function removeVarPrefix(value: string) {
    return value.replace('var(', '').replace(')', '')
  }

  function removeRgbPrefix(value: string) {
    return value.replace('rgb(', '').replace(')', '')
  }

  for (const [key, tokenValues] of Object.entries(themeVars)) {
    for (const [tokenKey, tokenValue] of Object.entries(tokenValues)) {
      let cssVarsKey = removeVarPrefix(tokenValue)
      let cssValue = tokens[key][tokenKey]

      if (key === 'colors') {
        cssVarsKey = removeRgbPrefix(cssVarsKey)
        const { r, g, b } = colord(cssValue).toRgb()
        cssValue = `${r} ${g} ${b}`
      }

      styles.push(`${cssVarsKey}: ${cssValue}`)
    }
  }

  const styleStr = styles.join(';')

  return styleStr
}
export function addThemeVarsToHtml(tokens: App.Theme.BaseToken, darkTokens: App.Theme.BaseToken) {
  const cssVarStr = getCssVarByTokens(tokens)
  const darkCssVarStr = getCssVarByTokens(darkTokens)

  const css = `
    html {
      ${cssVarStr}
    }
  `

  const darkCss = `
    html.${DARK_CLASS} {
      ${darkCssVarStr}
    }
  `

  const styleId = 'theme-vars'

  const style = document.querySelector(`#${styleId}`) || document.createElement('style')

  style.id = styleId

  style.textContent = css + darkCss

  document.head.appendChild(style)
}
